import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, IconButton, Stack, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import {
  CloseRounded as CloseIcon,
  DragIndicatorRounded as DragIcon,
  KeyboardArrowDownRounded as ExpandIcon,
  KeyboardArrowUpRounded as CollapseIcon,
} from '@mui/icons-material';
import type { ViewportOcclusionRect } from '../../models/types';

export interface FloatingCardPosition { x: number; y: number }
export interface FloatingCardSize { width: number; height: number }

type ResizeEdge = 'top' | 'right' | 'bottom' | 'left' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

interface PersistedFloatingCardState {
  position?: FloatingCardPosition;
  size?: FloatingCardSize;
  collapsed?: boolean;
}

export interface FloatingWorkspaceCardProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  position?: FloatingCardPosition;
  defaultPosition: FloatingCardPosition;
  size?: FloatingCardSize;
  defaultSize: FloatingCardSize;
  minSize?: FloatingCardSize;
  maxSize?: Partial<FloatingCardSize>;
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onPositionChange?: (position: FloatingCardPosition) => void;
  onSizeChange?: (size: FloatingCardSize) => void;
  onCollapsedChange?: (collapsed: boolean) => void;
  onClose?: () => void;
  onOcclusionRectChange?: (rect: ViewportOcclusionRect | null) => void;
  persistenceKey?: string;
  accentColor?: string;
  headerActions?: React.ReactNode;
  dragSurface?: 'header' | 'card';
  viewportMargin?: number;
  zIndex?: number;
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  testId?: string;
  layoutMode?: 'floating' | 'bottom-sheet';
  bottomSheetHeight?: number;
}

const HANDLE_EDGE = 8;
const HANDLE_CORNER = 13;
const HEADER_HEIGHT = 44;

const getViewport = () => ({
  width: typeof window === 'undefined' ? 1440 : window.innerWidth,
  height: typeof window === 'undefined' ? 900 : window.innerHeight,
});

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), Math.max(min, max));

const readPersistedState = (key?: string): PersistedFloatingCardState => {
  if (!key || typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.localStorage.getItem(`explan:floating-card:${key}`) || '{}');
  } catch {
    return {};
  }
};

const isNoDragTarget = (target: EventTarget | null): boolean => (
  target instanceof Element && Boolean(target.closest([
    '[data-floating-no-drag="true"]',
    '[data-timeline-no-drag="true"]',
    'button',
    'input',
    'textarea',
    'select',
    '[role="button"]',
    '[role="slider"]',
    '[role="menuitem"]',
  ].join(',')))
);

const cursorForEdge = (edge: ResizeEdge): string => {
  if (edge === 'left' || edge === 'right') return 'ew-resize';
  if (edge === 'top' || edge === 'bottom') return 'ns-resize';
  return edge === 'top-left' || edge === 'bottom-right' ? 'nwse-resize' : 'nesw-resize';
};

const toOcclusionRect = (rect: DOMRect): ViewportOcclusionRect => ({
  left: rect.left,
  top: rect.top,
  right: rect.right,
  bottom: rect.bottom,
  width: rect.width,
  height: rect.height,
});

const FloatingWorkspaceCard: React.FC<FloatingWorkspaceCardProps> = ({
  title,
  subtitle,
  children,
  position,
  defaultPosition,
  size,
  defaultSize,
  minSize = { width: 360, height: 220 },
  maxSize,
  collapsed,
  defaultCollapsed = false,
  onPositionChange,
  onSizeChange,
  onCollapsedChange,
  onClose,
  onOcclusionRectChange,
  persistenceKey,
  accentColor = '#2563eb',
  headerActions,
  dragSurface = 'header',
  viewportMargin = 12,
  zIndex = 30,
  sx,
  contentSx,
  testId = 'floating-workspace-card',
  layoutMode = 'floating',
  bottomSheetHeight,
}) => {
  const theme = useTheme();
  const persisted = useMemo(() => readPersistedState(persistenceKey), [persistenceKey]);
  const [internalPosition, setInternalPosition] = useState(persisted.position ?? defaultPosition);
  const [internalSize, setInternalSize] = useState(persisted.size ?? defaultSize);
  const [internalCollapsed, setInternalCollapsed] = useState(persisted.collapsed ?? defaultCollapsed);
  const [viewport, setViewport] = useState(getViewport);
  const [dragging, setDragging] = useState(false);
  const [resizeEdge, setResizeEdge] = useState<ResizeEdge | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const restoredControlledStateRef = useRef(false);
  const skipFirstControlledPersistRef = useRef(Boolean(
    (position && persisted.position) ||
    (size && persisted.size) ||
    (collapsed != null && persisted.collapsed != null)
  ));
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origin: FloatingCardPosition;
  } | null>(null);
  const resizeRef = useRef<{
    pointerId: number;
    edge: ResizeEdge;
    startX: number;
    startY: number;
    originPosition: FloatingCardPosition;
    originSize: FloatingCardSize;
  } | null>(null);

  const resolvedPosition = position ?? internalPosition;
  const resolvedSize = size ?? internalSize;
  const isCollapsed = collapsed ?? internalCollapsed;
  const maximumSize = {
    width: Math.max(minSize.width, Math.min(maxSize?.width ?? viewport.width, viewport.width - (viewportMargin * 2))),
    height: Math.max(minSize.height, Math.min(maxSize?.height ?? viewport.height, viewport.height - (viewportMargin * 2))),
  };
  const clampedSize = {
    width: clamp(resolvedSize.width, minSize.width, maximumSize.width),
    height: clamp(resolvedSize.height, minSize.height, maximumSize.height),
  };
  const displayedHeight = isCollapsed ? HEADER_HEIGHT : clampedSize.height;
  const isBottomSheet = layoutMode === 'bottom-sheet';
  const resolvedBottomSheetHeight = isCollapsed
    ? HEADER_HEIGHT
    : Math.min(bottomSheetHeight ?? clampedSize.height, Math.floor(viewport.height * 0.68));
  const clampedPosition = {
    x: clamp(resolvedPosition.x, viewportMargin, viewport.width - clampedSize.width - viewportMargin),
    y: clamp(resolvedPosition.y, viewportMargin, viewport.height - displayedHeight - viewportMargin),
  };
  const clampedX = clampedPosition.x;
  const clampedY = clampedPosition.y;
  const clampedWidth = clampedSize.width;
  const clampedHeight = clampedSize.height;

  const updatePosition = useCallback((next: FloatingCardPosition) => {
    if (!Number.isFinite(next.x) || !Number.isFinite(next.y)) return;
    if (position == null) setInternalPosition((current) => (
      current.x === next.x && current.y === next.y ? current : next
    ));
    onPositionChange?.(next);
  }, [onPositionChange, position]);

  const updateSize = useCallback((next: FloatingCardSize) => {
    if (!Number.isFinite(next.width) || !Number.isFinite(next.height)) return;
    if (size == null) setInternalSize((current) => (
      current.width === next.width && current.height === next.height ? current : next
    ));
    onSizeChange?.(next);
  }, [onSizeChange, size]);

  const updateCollapsed = useCallback((next: boolean) => {
    if (collapsed == null) setInternalCollapsed(next);
    onCollapsedChange?.(next);
  }, [collapsed, onCollapsedChange]);

  useEffect(() => {
    if (restoredControlledStateRef.current) return;
    restoredControlledStateRef.current = true;
    if (position && persisted.position) onPositionChange?.(persisted.position);
    if (size && persisted.size) onSizeChange?.(persisted.size);
    if (collapsed != null && persisted.collapsed != null) onCollapsedChange?.(persisted.collapsed);
  }, [collapsed, onCollapsedChange, onPositionChange, onSizeChange, persisted, position, size]);

  useEffect(() => {
    if (!persistenceKey || typeof window === 'undefined') return;
    if (skipFirstControlledPersistRef.current) {
      skipFirstControlledPersistRef.current = false;
      return;
    }
    window.localStorage.setItem(`explan:floating-card:${persistenceKey}`, JSON.stringify({
      position: { x: clampedX, y: clampedY },
      size: { width: clampedWidth, height: clampedHeight },
      collapsed: isCollapsed,
    }));
  }, [clampedHeight, clampedWidth, clampedX, clampedY, isCollapsed, persistenceKey]);

  useEffect(() => {
    const handleViewportResize = () => setViewport(getViewport());
    window.addEventListener('resize', handleViewportResize);
    return () => window.removeEventListener('resize', handleViewportResize);
  }, []);

  useEffect(() => {
    if (resolvedPosition.x !== clampedX || resolvedPosition.y !== clampedY) {
      updatePosition({ x: clampedX, y: clampedY });
    }
    if (resolvedSize.width !== clampedWidth || resolvedSize.height !== clampedHeight) {
      updateSize({ width: clampedWidth, height: clampedHeight });
    }
  }, [clampedHeight, clampedWidth, clampedX, clampedY, resolvedPosition.x, resolvedPosition.y, resolvedSize.height, resolvedSize.width, updatePosition, updateSize]);

  useEffect(() => {
    if (!onOcclusionRectChange) return undefined;
    const publish = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      onOcclusionRectChange(rect && rect.width > 0 && rect.height > 0 ? toOcclusionRect(rect) : null);
    };
    publish();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    if (observer && rootRef.current) observer.observe(rootRef.current);
    window.addEventListener('resize', publish);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', publish);
      onOcclusionRectChange(null);
    };
  }, [clampedPosition.x, clampedPosition.y, clampedSize.height, clampedSize.width, isCollapsed, onOcclusionRectChange]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (drag && drag.pointerId === event.pointerId) {
        event.preventDefault();
        const clientX = Number.isFinite(event.clientX) ? event.clientX : drag.startX;
        const clientY = Number.isFinite(event.clientY) ? event.clientY : drag.startY;
        const next = {
          x: drag.origin.x + clientX - drag.startX,
          y: drag.origin.y + clientY - drag.startY,
        };
        updatePosition({
          x: clamp(next.x, viewportMargin, viewport.width - clampedSize.width - viewportMargin),
          y: clamp(next.y, viewportMargin, viewport.height - displayedHeight - viewportMargin),
        });
      }
      const activeResize = resizeRef.current;
      if (activeResize && activeResize.pointerId === event.pointerId) {
        event.preventDefault();
        const current = activeResize;
        const deltaX = (Number.isFinite(event.clientX) ? event.clientX : current.startX) - current.startX;
        const deltaY = (Number.isFinite(event.clientY) ? event.clientY : current.startY) - current.startY;
        const right = current.originPosition.x + current.originSize.width;
        const bottom = current.originPosition.y + current.originSize.height;
        let nextPosition = { ...current.originPosition };
        let nextSize = { ...current.originSize };

        if (current.edge.includes('left')) {
          nextSize.width = clamp(current.originSize.width - deltaX, minSize.width, maximumSize.width);
          nextPosition.x = right - nextSize.width;
        } else if (current.edge.includes('right')) {
          nextSize.width = clamp(current.originSize.width + deltaX, minSize.width, Math.min(maximumSize.width, viewport.width - current.originPosition.x - viewportMargin));
        }
        if (current.edge.includes('top')) {
          nextSize.height = clamp(current.originSize.height - deltaY, minSize.height, maximumSize.height);
          nextPosition.y = bottom - nextSize.height;
        } else if (current.edge.includes('bottom')) {
          nextSize.height = clamp(current.originSize.height + deltaY, minSize.height, Math.min(maximumSize.height, viewport.height - current.originPosition.y - viewportMargin));
        }
        nextPosition = {
          x: clamp(nextPosition.x, viewportMargin, viewport.width - nextSize.width - viewportMargin),
          y: clamp(nextPosition.y, viewportMargin, viewport.height - nextSize.height - viewportMargin),
        };
        updatePosition(nextPosition);
        updateSize(nextSize);
      }
    };
    const handlePointerUp = (event: PointerEvent) => {
      if (dragRef.current && dragRef.current.pointerId === event.pointerId) {
        dragRef.current = null;
        setDragging(false);
      }
      if (resizeRef.current && resizeRef.current.pointerId === event.pointerId) {
        resizeRef.current = null;
        setResizeEdge(null);
      }
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [clampedSize.width, displayedHeight, maximumSize.height, maximumSize.width, minSize.height, minSize.width, updatePosition, updateSize, viewport.height, viewport.width, viewportMargin]);

  const beginDrag = (event: React.PointerEvent<HTMLElement>) => {
    if ((event.button != null && event.button !== 0) || isNoDragTarget(event.target)) return;
    event.preventDefault();
    setDragging(true);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: Number.isFinite(event.clientX) ? event.clientX : 0,
      startY: Number.isFinite(event.clientY) ? event.clientY : 0,
      origin: clampedPosition,
    };
  };

  const beginResize = (edge: ResizeEdge) => (event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.button != null && event.button !== 0) || isCollapsed) return;
    event.preventDefault();
    event.stopPropagation();
    setResizeEdge(edge);
    resizeRef.current = {
      pointerId: event.pointerId,
      edge,
      startX: Number.isFinite(event.clientX) ? event.clientX : 0,
      startY: Number.isFinite(event.clientY) ? event.clientY : 0,
      originPosition: clampedPosition,
      originSize: clampedSize,
    };
  };

  const resizeHandle = (edge: ResizeEdge, handleSx: SxProps<Theme>) => (
    <Box
      key={edge}
      data-testid={`${testId}-resize-${edge}`}
      data-floating-no-drag="true"
      onPointerDown={beginResize(edge)}
      sx={{
        position: 'absolute',
        zIndex: edge.includes('-') ? 6 : 5,
        cursor: cursorForEdge(edge),
        touchAction: 'none',
        bgcolor: resizeEdge === edge ? alpha(accentColor, 0.16) : 'transparent',
        '&:hover': { bgcolor: alpha(accentColor, 0.1) },
        ...handleSx,
      }}
    />
  );

  return (
    <Box
      ref={rootRef}
      data-testid={testId}
      data-collapsed={String(isCollapsed)}
      data-layout-mode={layoutMode}
      onPointerDown={!isBottomSheet && dragSurface === 'card' ? beginDrag : undefined}
      onWheelCapture={(event) => event.stopPropagation()}
      sx={{
        position: 'absolute',
        left: clampedPosition.x,
        top: clampedPosition.y,
        width: clampedSize.width,
        height: displayedHeight,
        zIndex,
        overflow: 'hidden',
        pointerEvents: 'auto',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: alpha(accentColor, theme.palette.mode === 'dark' ? 0.34 : 0.22),
        bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.9 : 0.94),
        backdropFilter: 'blur(12px)',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 18px 48px rgba(2,6,23,0.42)'
          : '0 18px 48px rgba(15,23,42,0.16)',
        userSelect: dragging || resizeEdge ? 'none' : 'auto',
        cursor: dragging ? 'grabbing' : 'default',
        ...(isBottomSheet ? {
          left: 0,
          right: 0,
          top: 'auto',
          bottom: 0,
          width: '100%',
          height: resolvedBottomSheetHeight,
          borderRadius: '16px 16px 0 0',
          borderBottom: 0,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 -18px 48px rgba(2,6,23,0.48)'
            : '0 -18px 48px rgba(15,23,42,0.18)',
        } : null),
        ...sx,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        onPointerDown={!isBottomSheet && dragSurface === 'header' ? beginDrag : undefined}
        sx={{
          minHeight: HEADER_HEIGHT,
          height: HEADER_HEIGHT,
          px: 1.15,
          flexShrink: 0,
          cursor: isBottomSheet ? 'default' : dragging ? 'grabbing' : 'grab',
          borderBottom: isCollapsed ? 0 : '1px solid',
          borderColor: 'divider',
          background: `linear-gradient(90deg, ${alpha(accentColor, 0.11)}, transparent 58%)`,
        }}
      >
        {isBottomSheet ? <Box sx={{ width: 26, height: 4, borderRadius: 999, bgcolor: 'text.disabled' }} /> : <DragIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={750} noWrap>{title}</Typography>
          {subtitle ? <Typography variant="caption" color="text.secondary" display="block" noWrap>{subtitle}</Typography> : null}
        </Box>
        <Box data-floating-no-drag="true" sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          {headerActions}
          <Tooltip title={isCollapsed ? 'Expand card' : 'Collapse card'}>
            <IconButton size="small" aria-label={isCollapsed ? 'Expand card' : 'Collapse card'} onClick={() => updateCollapsed(!isCollapsed)}>
              {isCollapsed ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {onClose ? (
            <Tooltip title="Close card">
              <IconButton size="small" aria-label="Close card" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </Stack>
      {!isCollapsed ? (
        <Box sx={{ minHeight: 0, flex: 1, overflow: 'hidden', ...contentSx }}>
          {children}
        </Box>
      ) : null}
      {!isCollapsed && !isBottomSheet ? <React.Fragment>
        {resizeHandle('top', { top: 0, left: HANDLE_CORNER, right: HANDLE_CORNER, height: HANDLE_EDGE })}
        {resizeHandle('right', { top: HANDLE_CORNER, right: 0, bottom: HANDLE_CORNER, width: HANDLE_EDGE })}
        {resizeHandle('bottom', { right: HANDLE_CORNER, bottom: 0, left: HANDLE_CORNER, height: HANDLE_EDGE })}
        {resizeHandle('left', { top: HANDLE_CORNER, bottom: HANDLE_CORNER, left: 0, width: HANDLE_EDGE })}
        {resizeHandle('top-left', { top: 0, left: 0, width: HANDLE_CORNER, height: HANDLE_CORNER })}
        {resizeHandle('top-right', { top: 0, right: 0, width: HANDLE_CORNER, height: HANDLE_CORNER })}
        {resizeHandle('bottom-left', { bottom: 0, left: 0, width: HANDLE_CORNER, height: HANDLE_CORNER })}
        {resizeHandle('bottom-right', { right: 0, bottom: 0, width: HANDLE_CORNER, height: HANDLE_CORNER })}
      </React.Fragment> : null}
    </Box>
  );
};

export default FloatingWorkspaceCard;
